import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Disclosure } from 'shared/constants';
import { useDisclosure, usePersonalLoan, useTelemetry } from 'shared/hooks';
import SignDisclosure from './SignDisclosure';

let watchId;

const SignDisclosureContainer = () => {
  const { uuid: personalLoanUuid } = useParams();
  const history = useHistory();
  const { personalLoan, error: personalLoanError } = usePersonalLoan();
  const {
    disclosuresList,
    downloadDocument,
    esignDisclosure,
    loading: disclosureLoading,
    error: disclosureError,
  } = useDisclosure(personalLoanUuid, personalLoan.status?.disclosurelistUuid);
  const { telemetry } = useTelemetry();

  const [activeDisclosure, setActiveDisclosure] = useState(null);
  const [geolocation, setGeolocation] = useState({ latitude: 0, longitude: 0 });
  const [file, setFile] = useState(null);

  // Moves Autopay Agreement to be second to last and PL Agreement to be last
  const documentOrder = [Disclosure.AUTOPAYAGREEMENT, Disclosure.PL_AGREEMENT];
  const unsignedDisclosures = (disclosuresList || [])
    .filter(
      (d) => d.type === Disclosure.CLOSING_DISCLOSURE && d.signatureRequired && !d.hasEsignature
    )
    .sort(
      (d1, d2) => documentOrder.indexOf(d1.document.type) - documentOrder.indexOf(d2.document.type)
    );

  useEffect(() => {
    if (window && 'geolocation' in window.navigator) {
      watchId = window.navigator.geolocation.watchPosition((p) => {
        setGeolocation({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
        });
      });
    }

    if (watchId) return window.navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    if (unsignedDisclosures.length && !activeDisclosure) {
      const [firstDisclosure] = unsignedDisclosures;
      setActiveDisclosure(firstDisclosure);
    }
  }, [unsignedDisclosures]);

  useEffect(() => {
    if (activeDisclosure) triggerDownload();
  }, [activeDisclosure]);

  const nextDisclosure = async () => {
    const nextDisclosureIndex = unsignedDisclosures.indexOf(activeDisclosure) + 1;
    const nextActiveDisclosure = unsignedDisclosures[nextDisclosureIndex];

    if (nextActiveDisclosure) setActiveDisclosure(nextActiveDisclosure);
    else {
      telemetry('plDocsReviewed');

      history.push(`/applications/${personalLoanUuid}/funding/intro`);
    }
  };

  const triggerDownload = async () => {
    const data = await downloadDocument(
      personalLoanUuid,
      personalLoan.status.disclosurelistUuid,
      activeDisclosure.document.uuid
    );
    setFile(new Blob([data], { type: 'application/pdf' }));
  };

  const handleSubmit = async (data) => {
    await esignDisclosure({
      applicationUuid: personalLoanUuid,
      documentUuid: data.document.uuid,
      latitude: geolocation.latitude,
      longitude: geolocation.longitude,
      consentToEsign: true,
    });

    nextDisclosure();
  };

  if (disclosureLoading || !unsignedDisclosures.length || !activeDisclosure) return null;

  const currentDisclosure =
    unsignedDisclosures.findIndex((d) => d.uuid === activeDisclosure.uuid) + 1;
  const totalDisclosures = unsignedDisclosures.length;
  const disclosureHeader = `${activeDisclosure.document.description} (${currentDisclosure} of ${totalDisclosures})`;

  return (
    <SignDisclosure
      handleSubmit={handleSubmit}
      disclosure={activeDisclosure}
      header={disclosureHeader}
      file={file}
      serverError={personalLoanError || disclosureError}
    />
  );
};

export default SignDisclosureContainer;
